import { useMemo } from 'react'

import type { ButtonGroupProps } from './buttonGroup.types'

import { styled, th, vui } from '../core'
import { cs, filterUndefined } from '../utils'
import { ButtonGroupProvider } from './context'
import { getInnerBorderColor } from './helpers'

export const ButtonGroupBase = styled.divBox<{ innerBorderColor: string }>`
  display: flex;

  // Border color between Buttons
  > *:not(:last-of-type):not(:focus) {
    border-right-color: ${p => th.color(p.innerBorderColor)};
  }

  // First Button
  > *:first-of-type:not(:last-of-type) {
    border-right-width: 1px;
  }

  // Middle Buttons
  > *:not(:first-of-type):not(:last-of-type) {
    border-left-width: 0px;
    border-right-width: 1px;
  }

  // Last Button
  > *:not(:first-of-type):last-of-type {
    border-left-width: 0px;
    margin-right: 0;
  }

  // Border color
  > *,
  *:hover {
    border-color: ${p => th.color(p.innerBorderColor)};
  }

  // Rounded variant base styles
  > .vui-button-variant-buttonGroupRounded {
    border-left-width: 1px !important;
    border-color: var(--vui-control-placeholder);
  }
`

/**
 * Organizes layout and display of multiple buttons. Controls border visual around and between the buttons.
 * Exposes some props to the children via context.
 */
export const ButtonGroup = vui<'div', ButtonGroupProps>((props, ref) => {
  const { className, disabled, innerBorderColor, size, variant = 'default', ...rest } = props

  const context = useMemo(
    () =>
      filterUndefined({
        disabled,
        size,
        variant: variant === 'default' ? 'buttonGroupDefault' : 'buttonGroupRounded',
      }),
    [disabled, size, variant],
  )

  return (
    <ButtonGroupProvider value={context}>
      <ButtonGroupBase
        className={cs('vui-buttonGroup', className, `vui-buttonGroup-variant-${variant}`)}
        {...{ innerBorderColor: innerBorderColor ? innerBorderColor : getInnerBorderColor(props), ref }}
        {...rest}
      />
    </ButtonGroupProvider>
  )
})

ButtonGroup.displayName = 'ButtonGroup'
export default ButtonGroup
